import styled from '@emotion/styled';
import {
  Card,
  token,
  createMediaQuery,
  Section,
  CheckCircleOutlineSmallIcon,
  AddCircleOutlineSmallIcon,
  NotIncludedOutlineSmallIcon,
  TextLabel,
} from '@angeplus/prever-component-lib';
import { useRouter } from 'next/router';

import { useFirstMonthPriceCoupon } from '../../modules/pricing/pricing.store';
import { PriceFirstMonthPlan } from './PriceFirstMonthPlan';
import { convertCentsToBRL } from '../../utils/convertCentsToBRL';
import {
  useBenefitsByCategoryGroup,
  usePlanVersionManifest,
} from '../admin/plan-versions/plan-version.store';
import { SkeletonCardPlanVersion } from './SkeletonCardPlanVersion';
import { useCurrentCoupon } from '../coupons/coupons.store';
import PlanItem from './PlanItem';
import { PlanVersionCaptions } from './PlanVersionCaptions';
import { Benefit } from '../plan-benefit/plan-benefit.types';
import { BenefitStatus } from '../admin/plan-versions/plan-version.type';
import { LogoMap } from './LogoMap';
import { DependentRule } from './DependentRule';
import { CardPlanVersionPrimaryButton } from './CardPlanVersionPrimaryButton';

type CardPlanVersionProps = {
  planVersionId: string;
  benefits: Benefit[];
};

const CardPlanVersion = (props: CardPlanVersionProps) => {
  const router = useRouter();
  const { data: planVersion, isLoading: isLoadingPlanVersionManifest } =
    usePlanVersionManifest(props.planVersionId);
  const slug = planVersion?.plan_distribution?.plan?.slug;
  const basePrice = planVersion?.subscription_rule?.base_value;
  const { benefitsWithCategoryGroup, isLoadingCategories } =
    useBenefitsByCategoryGroup(props.benefits, planVersion);

  const { data: coupon } = useCurrentCoupon();
  const { firstMonth, hasFirstMonth } = useFirstMonthPriceCoupon({
    price: convertCentsToBRL(basePrice),
    ...coupon,
  });

  const isLoading = isLoadingPlanVersionManifest || isLoadingCategories;

  const redirectToDetails = () => {
    router.push(`/details/${props.planVersionId}`);
  };

  if (isLoading) {
    return <SkeletonCardPlanVersion />;
  }

  return (
    <Card
      labelButtonSecondary="Ver mais detalhes"
      onClickButtonSecondary={redirectToDetails}
    >
      <Item>
        {LogoMap[slug]}
        <div>
          <PriceFirstMonthPlan
            price={basePrice}
            firstMonthPrice={firstMonth}
            hasFirstMonthPrice={hasFirstMonth}
          />
        </div>
        <Section>
          <DependentRule planVersion={planVersion} />
          <ContainerPlanBenefitGroups>
            {Object.entries(benefitsWithCategoryGroup)?.map(
              ([groupName, items]) => (
                <div key={groupName}>
                  <ContainerPlanBenefitItems>
                    <TextLabel>{groupName}</TextLabel>
                    {items?.map((item) => {
                      const label =
                        item.name === 'Translado' ? item.transfer : item.name;

                      return (
                        <PlanItem
                          key={item.id}
                          label={label}
                          icon={getStatusPlanItem(item?.status)}
                        />
                      );
                    })}
                  </ContainerPlanBenefitItems>
                </div>
              ),
            )}
          </ContainerPlanBenefitGroups>
          <PlanVersionCaptions />
        </Section>
      </Item>
      <CardPlanVersionPrimaryButton
        planVersionId={props.planVersionId}
        contractProvider={planVersion?.contract_provider}
      />
    </Card>
  );
};

const ContainerPlanBenefitItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${token.spacing.spacingXxxSmall};
`;

const ContainerPlanBenefitGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${token.spacing.spacingSmall};
`;

export const getStatusPlanItem = (status: BenefitStatus) => {
  return icons[status];
};

const icons = {
  active: (
    <CheckCircleOutlineSmallIcon color={token.colors.feedbackSuccessPrimary} />
  ),
  inactive: <NotIncludedOutlineSmallIcon color={token.colors.neutralLight} />,
  optional: <AddCircleOutlineSmallIcon color={token.colors.accentPrimary} />,
};

const Item = styled.li`
  display: flex;
  flex-direction: column;
  width: 250px;
  background: ${token.colors.neutralXxLight};
  border-radius: ${token.borderRadius.borderRadiusMedium};
  gap: ${token.spacing.spacingSmall};

  ${createMediaQuery({
    desktop: {
      minWidth: 'unset',
    },
  })}
`;

export default CardPlanVersion;
